/**
 * hauler_disposal_rates + hauler_disposal_exclusions — Phase 28 / task 28.1 (DEC-331,
 * migration 0151). See the migration header for the rules; the API reads and writes
 * both with raw SQL, and this mirror keeps schema-diff tooling aligned.
 *
 * - Rates: what a hauler pays per bbl at one place, from a LOCAL date, in HUNDREDTHS OF A
 *   CENT ($0.425 = 4250). Append-only: a change is a new row.
 * - Exclusions: "never send this customer's water here" — per customer and place, with a
 *   required reason. Ended, never deleted.
 *
 * Each targets EXACTLY ONE of a platform disposal facility or a dropoff-capable hauler
 * location, mirroring jobs.planned_/actual_*.
 */

import { sql } from 'drizzle-orm';
import {
  boolean,
  check,
  date,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { disposalFacilities } from './disposals.js';
import { haulerDisposalFacilities, haulerLocations, haulerOperators } from './platform-catalog.js';
import { tenants, users } from './users.js';

const tenantIsolation = (name: string) =>
  pgPolicy(name, {
    as: 'permissive',
    to: appUserRole,
    for: 'all',
    using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
  });

export const haulerDisposalRates = pgTable(
  'hauler_disposal_rates',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    disposalFacilityId: uuid('disposal_facility_id').references(() => disposalFacilities.id, {
      onDelete: 'restrict',
    }),
    dropoffHaulerLocationId: uuid('dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    // 0208 — or a catalog disposal well the hauler authorized (SWD destination A1).
    disposalWellId: uuid('disposal_well_id').references(() => haulerDisposalFacilities.id, {
      onDelete: 'restrict',
    }),
    rateHundredthsCentPerBbl: integer('rate_hundredths_cent_per_bbl').notNull(),
    effectiveFrom: date('effective_from', { mode: 'string' }).notNull(),
    notes: text('notes'),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      'hauler_disposal_rates_one_target',
      sql`num_nonnulls(disposal_facility_id, dropoff_hauler_location_id, disposal_well_id) = 1`,
    ),
    check(
      'hauler_disposal_rates_rate_range',
      sql`rate_hundredths_cent_per_bbl BETWEEN 0 AND 1000000`,
    ),
    uniqueIndex('hauler_disposal_rates_facility_day_uq')
      .on(t.tenantId, t.disposalFacilityId, t.effectiveFrom)
      .where(sql`disposal_facility_id IS NOT NULL`),
    uniqueIndex('hauler_disposal_rates_location_day_uq')
      .on(t.tenantId, t.dropoffHaulerLocationId, t.effectiveFrom)
      .where(sql`dropoff_hauler_location_id IS NOT NULL`),
    uniqueIndex('hauler_disposal_rates_well_day_uq')
      .on(t.tenantId, t.disposalWellId, t.effectiveFrom)
      .where(sql`disposal_well_id IS NOT NULL`),
    tenantIsolation('hauler_disposal_rates_isolation'),
  ],
);

export const haulerDisposalExclusions = pgTable(
  'hauler_disposal_exclusions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerOperatorId: uuid('hauler_operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    disposalFacilityId: uuid('disposal_facility_id').references(() => disposalFacilities.id, {
      onDelete: 'restrict',
    }),
    dropoffHaulerLocationId: uuid('dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    // 0208 — or a catalog disposal well the hauler authorized (SWD destination A1).
    disposalWellId: uuid('disposal_well_id').references(() => haulerDisposalFacilities.id, {
      onDelete: 'restrict',
    }),
    reason: text('reason').notNull(),
    isActive: boolean('is_active').notNull().default(true),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    deactivatedBy: uuid('deactivated_by').references(() => users.id, { onDelete: 'set null' }),
    deactivatedAt: timestamp('deactivated_at', { withTimezone: true }),
  },
  (t) => [
    check(
      'hauler_disposal_exclusions_one_target',
      sql`num_nonnulls(disposal_facility_id, dropoff_hauler_location_id, disposal_well_id) = 1`,
    ),
    check('hauler_disposal_exclusions_reason_present', sql`length(btrim(reason)) > 0`),
    check(
      'hauler_disposal_exclusions_ended_consistently',
      sql`is_active = (deactivated_at IS NULL)`,
    ),
    uniqueIndex('hauler_disposal_exclusions_facility_active_uq')
      .on(t.tenantId, t.haulerOperatorId, t.disposalFacilityId)
      .where(sql`is_active AND disposal_facility_id IS NOT NULL`),
    uniqueIndex('hauler_disposal_exclusions_location_active_uq')
      .on(t.tenantId, t.haulerOperatorId, t.dropoffHaulerLocationId)
      .where(sql`is_active AND dropoff_hauler_location_id IS NOT NULL`),
    uniqueIndex('hauler_disposal_exclusions_well_active_uq')
      .on(t.tenantId, t.haulerOperatorId, t.disposalWellId)
      .where(sql`is_active AND disposal_well_id IS NOT NULL`),
    tenantIsolation('hauler_disposal_exclusions_isolation'),
  ],
);

export type HaulerDisposalRate = typeof haulerDisposalRates.$inferSelect;
export type HaulerDisposalExclusion = typeof haulerDisposalExclusions.$inferSelect;
